// canvasItems — derives the canvas's NESTED view of a page from the flat
// builder model. The flat list is the source of truth (the wire contract is
// flat), and membership is EXPLICIT twice over: a field belongs to a section
// iff its `sectionId` names that section's row — a top-level field may sit
// AFTER a section (confirmed), so position alone can't decide ownership — and
// a field is a conditional-logic FOLLOW-UP iff its `triggerParentId` names the
// field whose rule spawned it (follow-ups nest under their parent's item, up
// to two levels, and never render as top-level cards). The ops keep a
// section's children and a parent's follow-up block contiguous right after
// their row, which is what the wire's position ordering expects; derivation is
// defensive about it anyway (a row whose sectionId/triggerParentId doesn't
// resolve renders top-level rather than being swallowed).

import type { BuilderField } from './builderModel';

export interface CanvasFieldItem {
  kind: 'field';
  field: BuilderField;
  /** Index in the page's flat `fields` list. */
  flat: number;
  /** Exclusive flat end of this row's BLOCK (row + trigger descendants) —
   *  stamped by deriveCanvasItems; blocks are contiguous, so a trailing drop
   *  gap binds here, never at `flat + 1`. */
  end: number;
  /** Conditional-logic follow-up questions spawned by this field's rules
   *  (recursive — a level-1 follow-up carries its own level-2 follow-ups). */
  followUps: CanvasFieldItem[];
}

export interface CanvasSectionItem {
  kind: 'section';
  /** The section row itself (a normal BuilderField of type `section`). */
  section: BuilderField;
  /** Flat index of the section row. */
  flat: number;
  /** Exclusive flat end of the whole section block (row + members + their
   *  follow-up blocks). */
  end: number;
  /** The fields grouped inside this section — follow-up rows are NOT members,
   *  they hang off their parent member's `followUps`. */
  children: CanvasFieldItem[];
}

export type CanvasItem = CanvasFieldItem | CanvasSectionItem;

const itemId = (it: CanvasItem) => (it.kind === 'section' ? it.section.id : it.field.id);

/** Flat range `[start, end)` an item occupies — the whole block: a field spans
 *  its trigger descendants, a section spans members plus THEIR descendants. */
const itemRange = (it: CanvasItem): [number, number] => [it.flat, it.end];

/** Flat range `[start, end)` of a field row plus its contiguous
 *  trigger-descendant block — the unit move/duplicate/delete operate on.
 *  (The follow-ups of a follow-up are inside: membership is the
 *  `triggerParentId` chain rooting at the row, not direct parentage.) */
export const fieldBlockRange = (
  fields: BuilderField[],
  fieldId: string,
): [number, number] | null => {
  const start = fields.findIndex((f) => f.id === fieldId);
  if (start < 0) return null;
  const inBlock = new Set<string>([fields[start].id]);
  let end = start + 1;
  while (end < fields.length) {
    const parent = fields[end].triggerParentId;
    if (!parent || !inBlock.has(parent)) break;
    inBlock.add(fields[end].id);
    end += 1;
  }
  return [start, end];
};

/** Group a page's flat field list into top-level canvas items. */
export const deriveCanvasItems = (fields: BuilderField[]): CanvasItem[] => {
  const items: CanvasItem[] = [];
  // Every field item ever created, keyed by field id — follow-up rows attach
  // to their parent's item wherever it lives (top-level, section member, or
  // itself a follow-up).
  const fieldItems = new Map<string, CanvasFieldItem>();

  for (let flat = 0; flat < fields.length; flat += 1) {
    const field = fields[flat];
    if (field.elementType === 'section') {
      items.push({ kind: 'section', section: field, flat, end: flat + 1, children: [] });
      continue;
    }

    const item: CanvasFieldItem = { kind: 'field', field, flat, end: flat + 1, followUps: [] };
    fieldItems.set(field.id, item);

    // A follow-up nests under its parent — checked BEFORE section membership,
    // since a follow-up inside a section carries the section's id too.
    const parentItem = field.triggerParentId ? fieldItems.get(field.triggerParentId) : undefined;
    if (parentItem) {
      parentItem.followUps.push(item);
      continue;
    }

    const last = items[items.length - 1];
    if (last?.kind === 'section' && field.sectionId === last.section.id) {
      last.children.push(item);
    } else {
      items.push(item);
    }
  }

  // Stamp block ends: blocks are contiguous, so every item's block runs to the
  // start of the NEXT row that belongs to an outer scope. Walking items back
  // to front, a top-level item ends where its successor starts; nested items
  // end where their next sibling starts, capped by the owner's end.
  let cursor = fields.length;
  const stampEnds = (list: CanvasFieldItem[], cap: number) => {
    let inner = cap;
    for (let i = list.length - 1; i >= 0; i -= 1) {
      list[i].end = inner;
      stampEnds(list[i].followUps, inner);
      inner = list[i].flat;
    }
  };
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const it = items[i];
    it.end = cursor;
    if (it.kind === 'section') {
      stampEnds(it.children, cursor);
    } else {
      stampEnds(it.followUps, cursor);
    }
    cursor = it.flat;
  }

  return items;
};

/** Flat index range `[start, end)` of a section's block (row + members and
 *  their follow-ups) — the unit that section move/duplicate/delete operate on.
 *  Follow-ups of a member carry the section's id too, so the contiguity walk
 *  is unchanged. */
export const sectionBlockRange = (
  fields: BuilderField[],
  sectionId: string,
): [number, number] | null => {
  const start = fields.findIndex((f) => f.id === sectionId);
  if (start < 0 || fields[start].elementType !== 'section') return null;
  let end = start + 1;
  while (end < fields.length && fields[end].sectionId === sectionId) end += 1;
  return [start, end];
};

/** Swap a TOP-LEVEL item (a field outside any section, or a whole section
 *  block) with its neighbor item — each moves with its whole block. Returns
 *  the list unchanged at the edges. Adjacent items' flat ranges are contiguous
 *  by construction, so the swap is a three-way splice. */
export const moveTopLevelItem = (
  fields: BuilderField[],
  elementId: string,
  dir: -1 | 1,
): BuilderField[] => {
  const items = deriveCanvasItems(fields);
  const p = items.findIndex((it) => itemId(it) === elementId);
  const q = p + dir;
  if (p < 0 || q < 0 || q >= items.length) return fields;
  const [aStart, aEnd] = itemRange(items[Math.min(p, q)]);
  const [bStart, bEnd] = itemRange(items[Math.max(p, q)]);
  return [
    ...fields.slice(0, aStart),
    ...fields.slice(bStart, bEnd),
    ...fields.slice(aStart, aEnd),
    ...fields.slice(bEnd),
  ];
};

/** Swap a section MEMBER with its neighbor member, each moving with its whole
 *  follow-up block. Bounded inside the member's section; follow-up rows are
 *  not members and are never swap targets. */
export const swapMemberBlocks = (
  fields: BuilderField[],
  fieldId: string,
  dir: -1 | 1,
): BuilderField[] => {
  const field = fields.find((f) => f.id === fieldId);
  if (!field || field.sectionId === null || field.triggerParentId !== null) return fields;
  const range = sectionBlockRange(fields, field.sectionId);
  if (!range) return fields;

  // Member block starts inside the section, in order.
  const starts: number[] = [];
  for (let i = range[0] + 1; i < range[1]; i += 1) {
    if (fields[i].triggerParentId === null) starts.push(i);
  }
  const own = fields.findIndex((f) => f.id === fieldId);
  const p = starts.indexOf(own);
  const q = p + dir;
  if (p < 0 || q < 0 || q >= starts.length) return fields;

  const blockEnd = (start: number) => fieldBlockRange(fields, fields[start].id)?.[1] ?? start + 1;
  const aStart = starts[Math.min(p, q)];
  const aEnd = blockEnd(aStart);
  const bStart = starts[Math.max(p, q)];
  const bEnd = blockEnd(bStart);
  return [
    ...fields.slice(0, aStart),
    ...fields.slice(bStart, bEnd),
    ...fields.slice(aStart, aEnd),
    ...fields.slice(bEnd),
  ];
};

/** Move a row's whole BLOCK (a field with its trigger descendants, or a
 *  section with its members and their follow-ups) to flat `index` — expressed
 *  in CURRENT coordinates, exactly as the drop gaps bind it — joining the
 *  section named by `sectionId` (null = top-level). The moved field row and
 *  every trigger descendant are restamped with the new membership (a follow-up
 *  lives wherever its parent lives); a section move restamps nothing (members
 *  keep naming the section, the row keeps null). Illegal moves return the
 *  input ARRAY UNCHANGED — same reference, so callers can skip the dirty
 *  flag: unknown or follow-up rows, a section targeting a section, an index
 *  that would split a trigger block or a section's contiguous run (the
 *  serializer derives sectionNumber from that contiguity), and moves that
 *  change neither position nor membership. */
export const moveBlockTo = (
  fields: BuilderField[],
  movedId: string,
  index: number,
  sectionId: string | null,
): BuilderField[] => {
  const moved = fields.find((f) => f.id === movedId);
  if (!moved || moved.triggerParentId !== null) return fields;
  const isSection = moved.elementType === 'section';
  if (isSection && sectionId !== null) return fields;
  const range = isSection ? sectionBlockRange(fields, movedId) : fieldBlockRange(fields, movedId);
  if (!range) return fields;
  const [start, end] = range;
  const target = Math.min(Math.max(index, 0), fields.length);

  const block = fields.slice(start, end);
  const rest = [...fields.slice(0, start), ...fields.slice(end)];
  // Removing the block shifts targets past it left by its length; the gaps
  // hugging the block (`target` at start or end — trailing gaps bind block
  // ends) both resolve to `at === start`: order-preserving, but possibly still
  // a MEMBERSHIP change (a top-level row dropped on the preceding section's
  // trailing gap joins it in place).
  const at = target <= start ? target : target >= end ? target - end + start : start;

  if (at === start && sectionId === moved.sectionId) return fields;

  // Contiguity guards, in `rest`/`at` coordinates — a row landing mid-block
  // would break the walks the section ops and the serializer rely on: never
  // split a trigger block; a member must sit inside its section's contiguous
  // run; a top-level row must not split that run (landing right after the
  // run's last member is fine — `rest[at]` is then already outside it).
  if (at < rest.length && rest[at].triggerParentId !== null) return fields;
  if (sectionId !== null) {
    const prev = rest[at - 1];
    if (!prev || (prev.id !== sectionId && prev.sectionId !== sectionId)) return fields;
  } else if (at < rest.length && rest[at].sectionId !== null) {
    return fields;
  }

  const stamped = isSection
    ? block
    : block.map((r) => (r.sectionId === sectionId ? r : { ...r, sectionId }));
  return [...rest.slice(0, at), ...stamped, ...rest.slice(at)];
};
